import classNames from "classnames";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import iconXClose from "../../../../img/icons/x-close.svg";
import { useAppSelector } from "../../reducers";
import { defaultState } from "../defaults";
import { AppliedFilters } from "./AppliedFilters";
import { Filter, IProps as IFilterProps } from "./Filter";

import styles from "./SidebarFilterSection.module.scss";

interface IProps extends IFilterProps {
    isMobile: boolean;
    showAppliedFilters: boolean;
    writeReviewURL: string;
    onToggleMobileSideNav: () => void;
    mobileSideNavOpen: boolean;
    isCondensedView?: boolean;
    isCollapsedView?: boolean;
    showProducts: boolean;
}

const buildCloseButton = (
    onToggleMobileSideNav: () => void,
): React.ReactNode => {
    return (
        <div className={styles.topbar}>
            <button
                className={styles.closeButton}
                onClick={onToggleMobileSideNav}
                aria-label={t`close`}
            >
                <SVG
                    className={styles.closeButtonSvg}
                    src={iconXClose}
                    role="img"
                    aria-hidden="true"
                />
            </button>
        </div>
    );
};

const buildWriteReviewButton = (writeReviewURL: string): React.ReactNode => {
    if (writeReviewURL === "") {
        return null;
    }
    return (
        <a className={styles.link} href={writeReviewURL}>
            <span className={styles.writeAReviewButton}>
                <span className={styles.writeAReviewButtonIcon}></span>
                <h3>{t`Write a Review`}</h3>
            </span>
        </a>
    );
};

const buildFilterButton = (
    onToggleMobileSideNav: () => void,
): React.ReactNode => {
    return (
        <button
            className={styles.filterToggleButton}
            onClick={onToggleMobileSideNav}
        >
            <h3>
                {t`Filter Reviews`}
                <span aria-hidden="true">
                    <sup>§</sup>
                </span>
            </h3>
        </button>
    );
};

export const SidebarFilterSection = (props: IProps): React.ReactNode => {
    const state = useAppSelector(
        (rootState) => rootState.reviews || defaultState,
    );

    const facets = state.data.facets;

    const textFacets = facets
        .filter((facet) => facet.type === "text")
        .map((facet) => (
            <Filter
                {...props}
                key={facet.type}
                facet={facet}
                isCollapsedView={props.isCollapsedView}
                isCondensedView={props.isCondensedView}
            />
        ));
    const otherFacets = facets
        .filter((facet) => facet.type !== "text")
        .map((facet) => (
            <Filter
                {...props}
                key={facet.type}
                facet={facet}
                isCollapsedView={props.isCollapsedView}
                isCondensedView={props.isCondensedView}
            />
        ));

    const filterButtonClasses = classNames({
        [styles.filterButtons]: true,
        [styles.filterButtonsMobile]: props.isMobile,
    });

    let sideNavContent: React.ReactNode;
    if (props.isMobile) {
        // hide reviews and show filter options
        if (props.mobileSideNavOpen) {
            sideNavContent = (
                <>
                    {buildCloseButton(props.onToggleMobileSideNav)}
                    {otherFacets}
                </>
            );
        } else {
            // show search box and filter button
            sideNavContent = (
                <>
                    {textFacets}
                    <div className={filterButtonClasses}>
                        {buildWriteReviewButton(props.writeReviewURL)}
                        {buildFilterButton(props.onToggleMobileSideNav)}
                    </div>
                    {props.showAppliedFilters && <AppliedFilters />}
                </>
            );
        }
    } else {
        // show search box, navbutton and filters
        sideNavContent = (
            <>
                {textFacets}
                {buildWriteReviewButton(props.writeReviewURL)}
                {otherFacets}
            </>
        );
    }

    const filterClasses = classNames({
        [styles.sideFilters]: true,
        [styles.sideFiltersMobile]: props.isMobile,
        [styles.sideFiltersDesktop]: !props.isMobile,
        "customer-review__side-filters": true,
        "customer-review__side-filters--desktop": !props.isMobile,
        "customer-review__side-filters--mobile": props.isMobile,
    });

    return <div className={filterClasses}>{sideNavContent}</div>;
};
